<script setup lang="ts">
import { ref, computed } from 'vue'
import { ChevronRight, Search } from 'lucide-vue-next'

/**
 * Collapsible filter facet — a manageable group of checkbox options with counts.
 * Used by the supplier directory and the account supplier matrix so large facet
 * lists (segments, industries, categories, …) stay tidy.
 */
const props = withDefaults(defineProps<{
  title: string
  items: [string, number][]      // [value, count], pre-sorted
  selected: Set<string>
  defaultOpen?: boolean
  initialLimit?: number          // rows shown before "show all"
  searchThreshold?: number       // show inline search when items exceed this
  labelFormat?: (v: string) => string
}>(), {
  defaultOpen: false,
  initialLimit: 6,
  searchThreshold: 10,
})

const emit = defineEmits<{ (e: 'toggle', value: string): void }>()

const open = ref(props.defaultOpen)
const query = ref('')
const expanded = ref(false)

const activeCount = computed(() => props.items.filter(([v]) => props.selected.has(v)).length)
const showSearch = computed(() => props.items.length > props.searchThreshold)

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return props.items
  return props.items.filter(([v]) => fmt(v).toLowerCase().includes(q))
})
// Selected rows always surface to the top so active filters stay visible when collapsed-to-limit.
const ordered = computed(() => {
  const sel = filtered.value.filter(([v]) => props.selected.has(v))
  const rest = filtered.value.filter(([v]) => !props.selected.has(v))
  return [...sel, ...rest]
})
const visible = computed(() => (expanded.value || query.value) ? ordered.value : ordered.value.slice(0, props.initialLimit))
const hiddenCount = computed(() => ordered.value.length - visible.value.length)

function fmt(v: string) { return props.labelFormat ? props.labelFormat(v) : v }
</script>

<template>
  <div class="fg" :class="{ 'is-open': open }">
    <button class="fg-head" @click="open = !open">
      <ChevronRight :size="13" class="fg-chev" />
      <span class="fg-title">{{ title }}</span>
      <span v-if="activeCount" class="fg-active">{{ activeCount }}</span>
      <span class="fg-total">{{ items.length }}</span>
    </button>

    <div v-if="open" class="fg-body">
      <div v-if="showSearch" class="fg-search">
        <Search :size="12" class="fg-search-i" />
        <input v-model="query" :placeholder="`Search ${title.toLowerCase()}…`" />
      </div>

      <label v-for="[val, n] in visible" :key="val" class="fg-row" :class="{ 'is-on': selected.has(val) }">
        <span class="fg-row-l">
          <input type="checkbox" :checked="selected.has(val)" @change="emit('toggle', val)" />
          <span class="fg-box"></span>
          <span class="fg-label">{{ fmt(val) }}</span>
        </span>
        <span class="fg-cnt">{{ n }}</span>
      </label>

      <div v-if="!visible.length" class="fg-empty">No matches</div>

      <button v-if="hiddenCount > 0 && !query" class="fg-more" @click="expanded = true">
        Show all {{ ordered.length }}
      </button>
      <button v-else-if="expanded && !query && ordered.length > initialLimit" class="fg-more" @click="expanded = false">
        Show less
      </button>
    </div>
  </div>
</template>

<style scoped>
.fg { border-top: 1px solid var(--cc-border); }
.fg-head {
  width: 100%;
  display: flex; align-items: center; gap: 7px;
  padding: 9px 2px;
  background: transparent; border: 0; cursor: pointer;
  text-align: left;
}
.fg-chev { color: var(--cc-text-3); transition: transform 150ms; flex-shrink: 0; }
.fg.is-open .fg-chev { transform: rotate(90deg); }
.fg-title {
  flex: 1; min-width: 0;
  font-size: 11px; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
  color: var(--cc-text-2);
}
.fg-active {
  font-family: var(--font-mono); font-size: 10px; font-weight: 700;
  color: var(--cc-btn-text, #fff); background: var(--cc-accent);
  padding: 1px 6px; border-radius: 999px;
}
.fg-total {
  font-family: var(--font-mono); font-size: 10px; font-weight: 600;
  color: var(--cc-text-3);
}
.fg-body { padding: 0 0 8px; display: flex; flex-direction: column; gap: 1px; }

.fg-search { position: relative; margin: 2px 0 6px; }
.fg-search-i { position: absolute; left: 8px; top: 50%; transform: translateY(-50%); color: var(--cc-text-3); }
.fg-search input {
  width: 100%; box-sizing: border-box;
  padding: 6px 8px 6px 26px;
  background: var(--cc-surface); border: 1px solid var(--cc-border); border-radius: 6px;
  font-size: 12px; color: var(--cc-text);
}
.fg-search input:focus { outline: none; border-color: var(--cc-accent); }

.fg-row {
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding: 4px 6px; border-radius: 6px; cursor: pointer;
  transition: background 120ms;
}
.fg-row:hover { background: color-mix(in srgb, var(--cc-text-3) 8%, transparent); }
.fg-row.is-on { background: color-mix(in srgb, var(--cc-accent) 10%, transparent); }
.fg-row-l { display: flex; align-items: center; gap: 8px; min-width: 0; }
.fg-row input { position: absolute; opacity: 0; width: 0; height: 0; }
.fg-box {
  flex-shrink: 0; width: 15px; height: 15px;
  border: 1.5px solid var(--cc-border); border-radius: 4px; background: var(--cc-surface);
  position: relative; transition: background 120ms, border-color 120ms;
}
.fg-row.is-on .fg-box { background: var(--cc-accent); border-color: var(--cc-accent); }
.fg-row.is-on .fg-box::after {
  content: ''; position: absolute; left: 4px; top: 1px;
  width: 4px; height: 8px;
  border: 2px solid var(--cc-btn-text, #fff); border-top: 0; border-left: 0;
  transform: rotate(45deg);
}
.fg-label {
  font-size: 12.5px; color: var(--cc-text);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.fg-cnt {
  flex-shrink: 0;
  font-family: var(--font-mono); font-variant-numeric: tabular-nums;
  font-size: 10.5px; color: var(--cc-text-3);
}
.fg-empty { font-size: 11px; color: var(--cc-text-3); font-style: italic; padding: 4px 6px; }
.fg-more {
  align-self: flex-start; margin-top: 4px; padding: 3px 4px;
  background: transparent; border: 0; cursor: pointer;
  font-size: 11px; font-weight: 600; color: var(--cc-accent);
}
.fg-more:hover { text-decoration: underline; }
</style>
